import type { ReactNode } from 'react'
import { Alert, Box, Button, Chip, Dialog, DialogContent, IconButton, Stack, TextField, Typography, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined'
import type { ProdutoCatalogo } from '../api'
import { capitalize } from '../lib/formato'
import { DialogTitle } from './dialog-componentes'
import { GaleriaProduto } from './galeria-produto'
import { CampoQuantidade, QUANTIDADE_MAXIMA } from './lista-itens-pedido'
import { PrecoProduto } from './preco-produto'
import { SeloNovo } from './selo-novo'

export type FormatoDetalhe = 'delivery' | 'tablet' | 'totem'

const SOBREPOSICAO_FOLHA = 20

const FONTE_TOTEM = { titulo: '16px', nome: '28px', texto: '14px', preco: '26px', aviso: '14px', acao: '20px', quantidade: '24px' }

interface DialogDetalheProps {
  aberto?: boolean
  aoFechar: () => void
  titulo?: string
  formato?: FormatoDetalhe
  children: ReactNode
}

export function DialogDetalhe({ aberto = true, aoFechar, titulo = 'Detalhes do item', formato = 'delivery', children }: DialogDetalheProps): JSX.Element {
  const theme = useTheme()
  const largo = useMediaQuery(theme.breakpoints.up('md'))
  const totem = formato === 'totem'
  const tamanho =
    formato === 'tablet'
      ? { width: 'calc(100% - 32px)', maxWidth: 'none', height: 'calc(100% - 32px)', maxHeight: 'none', m: 2 }
      : totem
        ? { width: 'calc(100% - 32px)', maxWidth: 1000, maxHeight: 'calc(100% - 32px)', m: 2 }
        : largo ? { height: '90vh', '@supports (height: 90dvh)': { height: '90dvh' } } : {}

  return (
    <Dialog
      open={aberto}
      onClose={aoFechar}
      fullScreen={formato === 'delivery' && !largo}
      fullWidth
      maxWidth={formato === 'delivery' ? 'md' : false}
      scroll="paper"
      slotProps={{
        paper: { sx: { position: 'relative', ...tamanho } },
      }}
    >
      <DialogTitle
        onClose={aoFechar}
        sx={{ pr: 7, flexShrink: 0, borderBottom: 1, borderColor: 'divider', ...(totem ? { fontSize: FONTE_TOTEM.titulo, py: 2.5, px: 4 } : {}) }}
      >
        {titulo}
      </DialogTitle>
      {children}
    </Dialog>
  )
}

interface AcaoDetalhe {
  rotulo: string
  desabilitada?: boolean
  aoAcionar: () => void
}

interface ConteudoDetalheProdutoProps {
  produto: ProdutoCatalogo
  formato?: FormatoDetalhe
  children?: ReactNode
  observacao?: { valor: string; aoMudar: (valor: string) => void }
  quantidade: number
  aoMudarQuantidade: (quantidade: number) => void
  convite?: string | null
  acao?: AcaoDetalhe
}

function SemImagem({ totem }: { totem: boolean }): JSX.Element {
  return (
    <Stack spacing={1} sx={{ alignItems: 'center' }}>
      <ImageNotSupportedOutlinedIcon sx={{ fontSize: totem ? 96 : 64, opacity: 0.24 }} />
      <Typography variant="body1" color="text.secondary" sx={totem ? { fontSize: FONTE_TOTEM.texto } : undefined}>
        Sem imagem
      </Typography>
    </Stack>
  )
}

export function ConteudoDetalheProduto({
  produto,
  formato = 'delivery',
  children,
  observacao,
  quantidade,
  aoMudarQuantidade,
  convite,
  acao,
}: ConteudoDetalheProdutoProps): JSX.Element {
  const theme = useTheme()
  const largo = useMediaQuery(theme.breakpoints.up('md'))
  const deitado = useMediaQuery('(orientation: landscape)')
  const totem = formato === 'totem'
  const ladoALado = totem || (formato === 'tablet' ? deitado : largo)
  const temImagem = produto.imagens.length > 0
  const composicao = produto.descricaoComposicao ?? produto.informacoesTecnicas
  const fonteTexto = totem ? { fontSize: FONTE_TOTEM.texto, lineHeight: 1.6 } : {}
  // No totem cada bloco respira mais: o cliente lê de pé, de longe.
  const espaco = totem ? { texto: 2, preco: 3, aviso: 3 } : { texto: 1, preco: 1, aviso: 1 }

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: ladoALado ? 'row' : 'column', flex: 1, minHeight: 0 }}>
        {ladoALado && (
          <Box
            sx={{
              width: '42%',
              flexShrink: 0,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: totem ? 4 : 3,
              bgcolor: 'action.hover',
            }}
          >
            {temImagem ? (
              <GaleriaProduto
                imagens={produto.imagens}
                alt={produto.descricao}
                opcoes={{ largura: 768, altura: 768 }}
                arredondamento={4}
                sx={{ width: '100%', maxHeight: '72vh', objectFit: 'cover' }}
              />
            ) : (
              <SemImagem totem={totem} />
            )}
          </Box>
        )}

        <DialogContent
          sx={{
            p: 2,
            flex: 1,
            minWidth: 0,
            ...(totem ? { '& .MuiAlert-message, & .MuiAlert-message .MuiTypography-root': { fontSize: FONTE_TOTEM.aviso } } : {}),
          }}
        >
          {!ladoALado && (
            <Box sx={{ mx: -2, mt: -2 }}>
              {temImagem ? (
                <GaleriaProduto
                  imagens={produto.imagens}
                  alt={produto.descricao}
                  opcoes={{ largura: 768, altura: 768, modo: 'cover' }}
                  recuoIndicadores={SOBREPOSICAO_FOLHA + 8}
                  sx={{
                    display: 'block',
                    width: '100%',
                    aspectRatio: '1 / 1',
                    maxHeight: '50vh',
                    '@supports (height: 50dvh)': { maxHeight: '50dvh' },
                    objectFit: 'cover',
                    bgcolor: 'action.hover',
                  }}
                />
              ) : (
                <Box sx={{ height: 200, pb: `${SOBREPOSICAO_FOLHA}px`, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover' }}>
                  <SemImagem totem={totem} />
                </Box>
              )}
            </Box>
          )}
          <Box
            sx={
              !ladoALado
                ? {
                  position: 'relative',
                  mx: -2,
                  mt: `-${SOBREPOSICAO_FOLHA}px`,
                  px: 2,
                  pt: 2.5,
                  bgcolor: 'background.paper',
                  borderRadius: '20px 20px 0 0',
                  boxShadow: '0 -8px 20px -12px rgba(0, 0, 0, 0.25)',
                }
                : undefined
            }
          >
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: (t) => (t.palette.mode === 'light' ? '#404040' : t.palette.text.primary),
                  ...(totem ? { fontSize: FONTE_TOTEM.nome, lineHeight: 1.25 } : {}),
                }}
              >
                {capitalize(produto.descricao)}
                {produto.novo && <SeloNovo />}
              </Typography>

              {produto.emPromocao && (
                <Chip
                  icon={<LocalOfferIcon />}
                  label="Em promoção"
                  color="error"
                  size={totem ? 'medium' : 'small'}
                  sx={totem ? { fontSize: FONTE_TOTEM.aviso } : undefined}
                />
              )}
            </Stack>

            {produto.descricaoCompleta && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: espaco.texto, ...fonteTexto }}>
                {produto.descricaoCompleta}
              </Typography>
            )}

            {composicao && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: espaco.texto, fontStyle: 'italic', ...fonteTexto }}>
                {composicao}
              </Typography>
            )}

            <PrecoProduto
              produto={produto}
              sx={{ mt: espaco.preco }}
              valor={{ variant: 'h6', color: 'text.secondary', sx: totem ? { fontSize: FONTE_TOTEM.preco } : undefined }}
            />

            {produto.advertencias && (
              <Alert severity="warning" sx={{ mt: espaco.aviso }}>
                {produto.advertencias}
              </Alert>
            )}
          </Box>

          {children}

          {observacao && (
            <Box sx={{ mt: 4 }}>
              <TextField
                label="Observação (opcional)"
                value={observacao.valor}
                onChange={(e) => observacao.aoMudar(e.target.value)}
                multiline
                fullWidth
                minRows={2}
                sx={totem ? { '& .MuiInputBase-root, & .MuiInputLabel-root': { fontSize: FONTE_TOTEM.texto } } : undefined}
              />
            </Box>
          )}
        </DialogContent>
      </Box>

      {acao ? (
        <Box sx={{ borderTop: 1, borderColor: 'divider', flexShrink: 0, ...(totem ? { pr: 2,pl: 4, py: 3 } : { p: 2 }) }}>
          {convite && (
            <Typography variant="body2" sx={{ color: 'warning.dark', fontWeight: 600, mb: 1, ...(totem ? { fontSize: FONTE_TOTEM.aviso, mb: 2 } : {}) }}>
              {convite}
            </Typography>
          )}

          <Stack direction="row" spacing={2} alignItems="center">
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={totem ? { '& .MuiInputBase-input': { fontSize: FONTE_TOTEM.quantidade } } : undefined}
            >
              <IconButton
                aria-label="Diminuir quantidade"
                onClick={() => aoMudarQuantidade(Math.max(1, quantidade - 1))}
                size={totem ? 'large' : 'small'}
                sx={totem ? { border: 1, borderColor: 'divider' } : undefined}
              >
                <RemoveIcon fontSize={totem ? 'large' : 'medium'} />
              </IconButton>

              <CampoQuantidade quantidade={quantidade} aoMudar={aoMudarQuantidade} rotulo="Quantidade" />

              <IconButton
                aria-label="Aumentar quantidade"
                onClick={() => aoMudarQuantidade(Math.min(QUANTIDADE_MAXIMA, quantidade + 1))}
                disabled={quantidade >= QUANTIDADE_MAXIMA}
                size={totem ? 'large' : 'small'}
                sx={totem ? { border: 1, borderColor: 'divider' } : undefined}
              >
                <AddIcon fontSize={totem ? 'large' : 'medium'} />
              </IconButton>
            </Stack>

            <Button
              variant="contained"
              fullWidth
              size={totem ? 'large' : 'medium'}
              disabled={acao.desabilitada}
              onClick={acao.aoAcionar}
              sx={{ flexGrow: 1, ...(totem ? { minHeight: 64, fontSize: FONTE_TOTEM.acao } : {}) }}
            >
              {acao.rotulo}
            </Button>
          </Stack>
        </Box>
      ) : (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Typography variant="body2" color="text.secondary" align="center">
            Modo somente visualização — pedidos não são aceitos por este canal.
          </Typography>
        </Box>
      )}
    </>
  )
}
